/**
 * The lowest zxcvbn score a new password may have: 0 is too guessable, 4 very unguessable. The
 * backend asks for less (@see \Api\Model\Table\HeraldUsersTable): its PHP port of zxcvbn doesn't
 * always agree with this one on the score.
 */
export const MIN_PASSWORD_SCORE = 3

/** Until zxcvbn has loaded, a new password only has to be this long. */
export const MIN_PASSWORD_LENGTH_WITHOUT_SCORE = 8

export type PasswordFormError = 'currentPasswordMissing' | 'passwordTooWeak' | 'passwordsDiffer'

export interface ChangePasswordFields {
    currentPassword: string
    password: string
    passwordConfirm: string
}

/**
 * Whether a new password is strong enough. `score` is its zxcvbn score, or null while zxcvbn is
 * still loading.
 */
export const isStrongEnoughPassword = (password: string, score: number | null): boolean =>
    score === null
        ? password.length >= MIN_PASSWORD_LENGTH_WITHOUT_SCORE
        : score >= MIN_PASSWORD_SCORE

/**
 * What is wrong with each field of the change-password form; fields without an error are left out.
 *
 * @param askCurrentPassword Whether the form asks for the current password: a valid reset link
 *   replaces it. Its strength isn't judged, older passwords may be weaker than today's rules.
 * @param score The zxcvbn score of the new password, or null while zxcvbn is still loading
 */
export const getChangePasswordErrors = (
    { currentPassword, password, passwordConfirm }: ChangePasswordFields,
    askCurrentPassword: boolean,
    score: number | null,
): Partial<Record<keyof ChangePasswordFields, PasswordFormError>> => {
    const errors: Partial<Record<keyof ChangePasswordFields, PasswordFormError>> = {}

    if (askCurrentPassword && currentPassword === '') {
        errors.currentPassword = 'currentPasswordMissing'
    }

    if (!isStrongEnoughPassword(password, score)) {
        errors.password = 'passwordTooWeak'
    }

    if (passwordConfirm !== password) {
        errors.passwordConfirm = 'passwordsDiffer'
    }

    return errors
}

/** What the change-password form shows for a rejection of the backend, besides `PasswordFormError`. */
export type ChangePasswordServerError = 'wrongCurrentPassword' | 'sameAsCurrent' | 'failed'

/**
 * The field of the change-password form that shows a rejection of the backend, and the error to
 * show there. An unknown rejection shows at the new password.
 *
 * @param error The `error` of the rejection (@see \Api\Controller\AuthController::changePassword())
 */
export const getChangePasswordServerError = (
    error: string | undefined,
): { field: keyof ChangePasswordFields; error: PasswordFormError | ChangePasswordServerError } => {
    switch (error) {
        case 'wrong current password':
            return { field: 'currentPassword', error: 'wrongCurrentPassword' }
        case 'same password':
            return { field: 'password', error: 'sameAsCurrent' }
        case 'password too weak':
            return { field: 'password', error: 'passwordTooWeak' }
        default:
            return { field: 'password', error: 'failed' }
    }
}

/** A loose check that catches typos; the backend decides whether the address exists. */
export const looksLikeEmail = (email: string): boolean => /[^@]+@[^.]+\..+/.test(email)
